import { getZoneAbbreviation } from '../config/timezones';

/**
 * Timezone utilities for role-based timezone display
 * EC-31: Always display timezone abbreviation with times
 * EC-32: Internal users see ET, external users see local timezone
 */

/** Eastern Time - the "working timezone" for internal Medley team */
export const INTERNAL_TIMEZONE = 'America/New_York';

/**
 * Determine if the current user is on an internal (admin) portal.
 * Coach portal lives under /coach/* - everything else is internal.
 */
export function isInternalPortal(): boolean {
  if (typeof window === 'undefined') return true;
  return !window.location.pathname.startsWith('/coach');
}

/**
 * Get the display timezone based on portal context.
 * Internal users (admin, CM): Eastern Time
 * External users (coach): Browser's local timezone
 */
export function getDisplayTimezone(): string {
  return isInternalPortal()
    ? INTERNAL_TIMEZONE
    : Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get a short timezone indicator (e.g., "ET", "PT", "CT")
 * Uses Intl shortGeneric for non-DST-specific names
 */
export function getTimezoneAbbreviation(timezone: string, date?: Date): string {
  const curated = getZoneAbbreviation(timezone);
  const read = (
    d: Date,
    timeZoneName: 'shortGeneric' | 'shortOffset'
  ): string | null => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName,
      }).formatToParts(d);
      return parts.find(p => p.type === 'timeZoneName')?.value ?? null;
    } catch {
      return null;
    }
  };

  const d = date || new Date();
  if (timezone === 'UTC') return 'UTC';

  // `shortGeneric` gives a real abbreviation for US zones (ET, CT, MT, PT) but
  // a phrase everywhere else: Europe/Paris becomes "France Time" and
  // Asia/Tokyo becomes "Japan Time". Those are far too long to sit beside a
  // time in a list, so keep it only when it is a genuine short code.
  const offset = read(d, 'shortOffset');

  // A curated code plus the live offset, e.g. "CET (GMT+2)" or
  // "IST (GMT+5:30)". The code says which zone, the offset says where its
  // clock is today. Showing only the code repeats the original bug: "CET" is
  // the winter name and Paris is CEST in summer.
  if (curated) return offset ? `${curated} (${offset})` : curated;

  const generic = read(d, 'shortGeneric');
  if (generic && /^[A-Z]{2,5}$/.test(generic)) {
    return offset ? `${generic} (${offset})` : generic;
  }

  // Otherwise use the offset, which is compact and unambiguous: GMT+9,
  // GMT+5:30. This is also more honest than the old hand-written map, which
  // labelled Europe/London "GMT" all year even though it is GMT+1 in summer.
  return offset ?? generic ?? timezone;
}

/**
 * Format a time in a specific timezone with abbreviation.
 * Useful for secondary timezone display.
 * @example formatTimeInTimezone('2024-01-15T14:30:00Z', 'America/Los_Angeles') // "6:30 AM PT"
 */
export function formatTimeInTimezone(
  date: string | Date | null | undefined,
  timezone: string
): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';

  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
    timeZoneName: 'shortGeneric',
  });
}

/**
 * Convert a UTC Date to a "display Date" that, when rendered by DateTimePicker
 * in the browser's local timezone, shows the correct time for the target timezone.
 *
 * For example, if the UTC date is 2026-03-15T13:00:00Z (9 AM ET) and the browser
 * is in Mountain Time (UTC-7), DateTimePicker would show 6:00 AM. This function
 * shifts the date so DateTimePicker displays 9:00 AM instead.
 *
 * @param utcDate - The real UTC Date from the API
 * @param timezone - The target timezone to display (e.g., 'America/New_York')
 * @returns A shifted Date that displays correctly in DateTimePicker
 */
export function utcToTimezoneDate(
  utcDate: Date | null,
  timezone: string
): Date | null {
  if (!utcDate) return null;

  // Get the time parts as they appear in the target timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(utcDate);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(p => p.type === type)?.value ?? '0';

  // Build a local Date with the timezone's wall-clock values
  return new Date(
    parseInt(get('year')),
    parseInt(get('month')) - 1,
    parseInt(get('day')),
    parseInt(get('hour')) % 24,
    parseInt(get('minute')),
    parseInt(get('second'))
  );
}

/**
 * Get the UTC offset (in milliseconds) of `timezone` at a given instant.
 *
 * Works by formatting the instant into the target timezone's wall-clock parts,
 * reinterpreting those parts as if they were UTC, and subtracting the original
 * instant. The result is positive for zones ahead of UTC and negative for zones
 * behind it (e.g. -4h for EDT, -5h for EST).
 *
 * This is intentionally independent of the browser's local timezone — it relies
 * only on `Intl.DateTimeFormat` (the IANA zone) and `Date.UTC` (offset-free),
 * never on the local `Date` constructor.
 */
function getTimezoneOffsetMs(instant: Date, timezone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find(p => p.type === type)?.value ?? '0');

  const wallClockAsUtcMs = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second')
  );

  return wallClockAsUtcMs - instant.getTime();
}

/**
 * Convert a "display Date" (from DateTimePicker) back to a proper UTC Date
 * for API submission. This reverses `utcToTimezoneDate`.
 *
 * The display Date's components (year, month, day, hour, minute) represent
 * wall-clock time in the target timezone. We interpret those components
 * in the target timezone to get the correct UTC instant.
 *
 * IMPORTANT: the offset is derived via `Date.UTC` + `Intl.DateTimeFormat`
 * (see `getTimezoneOffsetMs`), NOT via the local `Date` constructor. The
 * previous implementation rebuilt a probe with `new Date(y, m, d, …)`, whose
 * offset collapsed to 0 whenever the browser's timezone equaled the target —
 * silently dropping the ET→UTC shift for ET (and most other) browsers. (MED-1023)
 *
 * @param displayDate - The Date from the form (wall-clock time in target TZ)
 * @param timezone - The target timezone (e.g., 'America/New_York')
 * @returns A Date representing the correct UTC instant
 */
export function timezoneDateToUtc(
  displayDate: Date | null,
  timezone: string
): Date | null {
  if (!displayDate) return null;

  // Read the wall-clock components the user sees in the picker (browser-local
  // numbers, which by our display convention represent the target-TZ wall clock).
  const wantAsUtcMs = Date.UTC(
    displayDate.getFullYear(),
    displayDate.getMonth(),
    displayDate.getDate(),
    displayDate.getHours(),
    displayDate.getMinutes(),
    displayDate.getSeconds()
  );

  // First guess: subtract the offset measured at the provisional instant, then
  // refine once so a wall clock that lands near a DST transition resolves to the
  // offset that actually applies at the corrected instant.
  let offsetMs = getTimezoneOffsetMs(new Date(wantAsUtcMs), timezone);
  let utcMs = wantAsUtcMs - offsetMs;
  offsetMs = getTimezoneOffsetMs(new Date(utcMs), timezone);
  utcMs = wantAsUtcMs - offsetMs;

  return new Date(utcMs);
}

/**
 * Format time with primary and secondary timezone display.
 * @example formatTimeWithSecondary(date, 'America/New_York', 'America/Los_Angeles')
 *   // "9:00 AM ET (6:00 AM PT)"
 */
export function formatTimeWithSecondary(
  date: string | Date | null | undefined,
  primaryTimezone: string,
  secondaryTimezone: string
): string {
  if (!date) return '—';
  const primary = formatTimeInTimezone(date, primaryTimezone);
  if (primary === '—') return '—';
  if (primaryTimezone === secondaryTimezone) return primary;
  const secondary = formatTimeInTimezone(date, secondaryTimezone);
  return `${primary} (${secondary})`;
}
